import React, { useState, useEffect } from 'react';
import { 
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  makeStyles,
  Typography,
  Chip,
  Box,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { simpleGCPSubnetSelectorApiRef } from '../../simpleApi';

const useStyles = makeStyles(theme => ({
  formControl: {
    minWidth: '100%',
    margin: theme.spacing(1, 0),
  },
  selectedSubnets: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  error: {
    color: theme.palette.error.main,
  },
}));

export interface GCPSubnetFieldUiOptions {
  subnetType?: string;
  title?: string;
  businessUnitField?: string;
  regionField?: string;
  envField?: string;
  landingZoneField?: string;
  autoSelectAll?: boolean;
}

export const GCPSubnetFieldComponent = (
  props: FieldExtensionComponentProps<string[], GCPSubnetFieldUiOptions>,
) => {
  const classes = useStyles();
  const gcpApi = useApi(simpleGCPSubnetSelectorApiRef);
  const { onChange, rawErrors, required, formData, uiSchema } = props;
  
  // Get field references from ui:options
  const uiOptions = uiSchema['ui:options'] || {};
  const businessUnitField = uiOptions.businessUnitField || 'businessUnit';
  const regionField = uiOptions.regionField || 'region';
  const envField = uiOptions.envField || 'env';
  const landingZoneField = uiOptions.landingZoneField || 'landingZone';
  
  // Debug logging for development
  if (process.env.NODE_ENV === 'development') {
    console.debug('GCP Subnet Field:', {
      fieldName: props.name,
      autoSelectAll: uiOptions.autoSelectAll,
      isHidden: uiSchema['ui:widget'] === 'hidden'
    });
  }
  
  // Get values from form data using the field references
  const allFormData = props.formContext?.formData || {};
  const businessUnit = allFormData[businessUnitField] || 'mgs';
  const region = allFormData[regionField] || 'us-east4';
  const env = allFormData[envField] || 'np';
  const landingZone = allFormData[landingZoneField];
  
  // Get title from schema title
  const title = props.schema?.title || 'Subnets';
  
    // All fields will show the same subnet list, differentiated by title only
  
  const [availableSubnets, setAvailableSubnets] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const selectedSubnets = formData || [];

  // Load subnets when business unit or region changes
  useEffect(() => {
    if (businessUnit && region) {
      loadSubnets();
    }
  }, [businessUnit, region, env, landingZone]);
  
  // Auto-select all subnets when available subnets change
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.debug('GCP Field Debug: Field name:', props.name);
      console.debug('GCP Field Debug: uiSchema:', props.uiSchema);
    }
    
    const uiOptions = props.uiSchema?.['ui:options'] as GCPSubnetFieldUiOptions;
    const shouldAutoSelect = uiOptions?.autoSelectAll || props.name === 'shared_vpc_subnets';
    
    if (process.env.NODE_ENV === 'development') {
      console.debug('GCP Field Debug: autoSelectAll from uiOptions:', uiOptions?.autoSelectAll);
      console.debug('GCP Field Debug: shouldAutoSelect:', shouldAutoSelect);
      console.debug('GCP Field Debug: availableSubnets.length:', availableSubnets.length);
      console.debug('GCP Field Debug: formData:', formData);
    }
    
    if (shouldAutoSelect && availableSubnets.length > 0 && (!formData || formData.length === 0)) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('AutoSelectAll triggered for field:', props.name, 'with subnets:', availableSubnets);
      }
      onChange(availableSubnets);
    }
  }, [availableSubnets, props.uiSchema, props.name]);

  const loadSubnets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('GCP Subnet Field: Loading subnets for', props.name, `${businessUnit}/${region}`);
      }
      
      const subnetsData = await gcpApi.getTemplateSubnets(businessUnit, region);
      
      // Extract all subnet names (same list for all field types)
      const subnetsList: string[] = [];
      if (subnetsData[businessUnit] && subnetsData[businessUnit][region]) {
        const regionData = subnetsData[businessUnit][region] as any;
        
        // Get all subnets from the 'np' environment (what the API returns)
        if (regionData['np'] && regionData['np'].names && Array.isArray(regionData['np'].names)) {
          subnetsList.push(...regionData['np'].names);
        }
      }
      
      setAvailableSubnets(subnetsList);
      
      // Auto-select all subnets if autoSelectAll is enabled or for shared_vpc_subnets
      const uiOptions = props.uiSchema?.['ui:options'] as GCPSubnetFieldUiOptions;
      const shouldAutoSelect = uiOptions?.autoSelectAll === true || props.name === 'shared_vpc_subnets';
      
      // Only auto-select if field is empty and we have subnets to select
      if (shouldAutoSelect && subnetsList.length > 0 && (!formData || formData.length === 0)) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('AutoSelectAll: Selecting', subnetsList.length, 'subnets for field:', props.name);
        }
        onChange(subnetsList);
      }
    } catch (err) {
      console.error('Error loading subnets:', err);
      setError(`Failed to load subnets: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubnetChange = (event: any) => {
    const value = event.target.value;
    const newSelectedSubnets = typeof value === 'string' ? value.split(',') : value;
    onChange(newSelectedSubnets);
  };

  const hasError = rawErrors && rawErrors.length > 0;
  
  // Check if this field should be hidden
  const isHidden = uiSchema['ui:widget'] === 'hidden';
  
  // If hidden and auto-select is enabled, just return null but still process data
  if (isHidden) {
    return null;
  }

  return (
    <Box>
      <FormControl className={classes.formControl} error={hasError}>
        <InputLabel id={`${props.name}-subnet-label`}>
          {title} {required && '*'}
        </InputLabel>
        <Select
          labelId={`${props.name}-subnet-label`}
          multiple
          value={selectedSubnets}
          onChange={handleSubnetChange}
          input={<OutlinedInput label={title} />}
          renderValue={(selected) => (
            <Box className={classes.selectedSubnets}>
              {(selected as string[]).map((value) => (
                <Chip key={value} label={value} size="small" />
              ))}
            </Box>
          )}
          disabled={loading || availableSubnets.length === 0}
        >
          {availableSubnets.map((subnet) => (
            <MenuItem key={subnet} value={subnet}>
              <Checkbox checked={selectedSubnets.indexOf(subnet) > -1} />
              <ListItemText primary={subnet} />
            </MenuItem>
          ))}
        </Select>
        
        {loading && (
          <Box className={classes.loading} mt={1}>
            <CircularProgress size={16} />
            <Typography variant="caption">Loading subnets...</Typography>
          </Box>
        )}
        
        {error && (
          <Typography variant="caption" className={classes.error}>
            {error}
          </Typography>
        )}
        
        {hasError && (
          <Typography variant="caption" className={classes.error}>
            {rawErrors?.[0]}
          </Typography>
        )}
        
        {!loading && !error && availableSubnets.length === 0 && (
          <Typography variant="caption" color="textSecondary">
            No subnets available for {businessUnit} in {region}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};